import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Flex,
  HStack,
  VStack,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Select,
  Switch,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  TagLabel,
  Radio,
  RadioGroup,
  Alert,
  AlertIcon,
  AlertDescription,
  Skeleton,
  Spinner
} from '@chakra-ui/react';
import {
  Search, Folder, Plus, Edit2, Trash2, Target, MoreVertical, Zap, RefreshCw,
} from 'lucide-react';
import Header from '../../../components/Header';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import axios from 'axios';

const getApiHeaders = () => ({
  'x-api-key': localStorage.getItem('api-key'),
  'x-access-token': localStorage.getItem('customer-portal'),
});
const API_URL = process.env.REACT_APP_API_URL;

const colorPresets = [
  { name: 'Blue',   value: '#3B82F6' },
  { name: 'Green',  value: '#10B981' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Orange', value: '#F59E0B' },
  { name: 'Pink',   value: '#EC4899' },
  { name: 'Teal',   value: '#14B8A6' },
  { name: 'Red',    value: '#EF4444' },
  { name: 'Indigo', value: 'var(--brand-primary)' },
];

const EVENTS_API = 'https://stgadminbe.xnotify.ai';

export default function ConversionEvents() {
  const [events, setEvents] = useState([]);
  const [conversionMap, setConversionMap] = useState({});
  const [groups, setGroups] = useState([]);
  const [localConfig, setLocalConfig] = useState({});
  const [savingSet, setSavingSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [eventAttributes, setEventAttributes] = useState({});
  const [attributesLoading, setAttributesLoading] = useState(new Set());

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all-groups');

  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#3B82F6');
  const [editingGroup, setEditingGroup] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [pendingAssignEventName, setPendingAssignEventName] = useState(null);

  const toast = useToast();
  const manageGroupsModal = useDisclosure();
  const deleteGroupModal = useDisclosure();
  const quickCreateModal = useDisclosure();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsData, conversationsData, groupsData] = await Promise.all([
        axios.post(`${API_URL}/fetch-unique-events`, {}, { headers: getApiHeaders() }).then(r => r.data),
        axios.get(`${API_URL}/event-conversations`, { params: { limit: 200 }, headers: getApiHeaders() }).then(r => r.data),
        axios.get(`${API_URL}/conversation-groups`, { params: { limit: 100 }, headers: getApiHeaders() }).then(r => r.data),
      ]);
      setEvents(eventsData?.data?.events || []);
      const convMap = {};
      (conversationsData?.data?.event_conversations || []).forEach((c) => {
        if (!convMap[c.event_name]) convMap[c.event_name] = c;
      });
      setConversionMap(convMap);
      setGroups(groupsData?.data?.conversation_groups || []);
    } catch {
      toast({ title: 'Failed to load data', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-fetch attributes for any event already saved with from_property
  useEffect(() => {
    events.forEach((ev) => {
      const cfg = getEffectiveConfig(ev.name);
      if (cfg?.value_config?.source_type === 'from_property' && eventAttributes[ev.name] === undefined) {
        fetchAttributes(ev.name);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, conversionMap]);

  const fetchAttributes = async (eventName) => {
    if (eventAttributes[eventName] !== undefined) return;
    setAttributesLoading((p) => new Set([...p, eventName]));
    try {
      const data = await axios.post(`${API_URL}/fetch-unique-attributes`, { event: eventName }, { headers: getApiHeaders() }).then(r => r.data);
      setEventAttributes((p) => ({ ...p, [eventName]: data?.data?.attributes || [] }));
    } catch {
      setEventAttributes((p) => ({ ...p, [eventName]: [] }));
    } finally {
      setAttributesLoading((p) => { const n = new Set(p); n.delete(eventName); return n; });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getGroupById = (id) => groups.find((g) => g._id === id);
  const isConversionEvent = (name) => {
    if (localConfig[name] === null) return false;
    if (localConfig[name]) return true;
    return !!conversionMap[name];
  };
  const getEffectiveConfig = (name) => {
    if (localConfig[name] === null) return null;
    if (localConfig[name]) return localConfig[name];
    return conversionMap[name] || null;
  };
  const isDirty = (name) => name in localConfig;

  // ── Toggle & config ───────────────────────────────────────────────────────
  const handleToggle = (name) => {
    const on = isConversionEvent(name);
    if (on) {
      setLocalConfig((p) => ({ ...p, [name]: null }));
    } else {
      const saved = conversionMap[name];
      setLocalConfig((p) => ({
        ...p,
        [name]: saved ? { ...saved } : { conversion_type: 'secondary', conversion_group: null, value_config: { source_type: 'no_value' } },
      }));
    }
  };

  const updateConfig = (name, updates) => {
    if (localConfig[name] === null) return;
    setLocalConfig((p) => ({
      ...p,
      [name]: { ...(conversionMap[name] || {}), ...(p[name] || {}), ...updates },
    }));
  };

  const handleCancel = (name) => {
    setLocalConfig((p) => { const n = { ...p }; delete n[name]; return n; });
  };

  const handleSave = async (name) => {
    const local = localConfig[name];
    const saved = conversionMap[name];
    setSavingSet((p) => new Set([...p, name]));
    try {
      if (local === null) {
        if (saved) {
          const d = await axios.delete(`${API_URL}/event-conversations/${saved._id}`, { headers: getApiHeaders() }).then(r => r.data);
          if (!d.status) throw new Error(d.message);
          setConversionMap((p) => { const n = { ...p }; delete n[name]; return n; });
        }
        toast({ title: 'Conversion removed', status: 'info', duration: 2000 });
      } else if (saved) {
        const body = { conversion_type: local.conversion_type || null, conversion_group: local.conversion_group || null, value_config: local.value_config || { source_type: 'no_value' }, status: 'active' };
        const d = await axios.put(`${API_URL}/event-conversations/${saved._id}`, body, { headers: getApiHeaders() }).then(r => r.data);
        if (!d.status) throw new Error(d.message);
        setConversionMap((p) => ({ ...p, [name]: d.data.event_conversation }));
        toast({ title: 'Conversion updated', status: 'success', duration: 2000 });
      } else {
        const body = { event_name: name, conversion_type: local.conversion_type || null, conversion_group: local.conversion_group || null, value_config: local.value_config || { source_type: 'no_value' }, status: 'active' };
        const d = await axios.post(`${API_URL}/event-conversations`, body, { headers: getApiHeaders() }).then(r => r.data);
        if (!d.status) throw new Error(d.message);
        setConversionMap((p) => ({ ...p, [name]: d.data.event_conversation }));
        toast({ title: 'Conversion saved', status: 'success', duration: 2000 });
      }
      setLocalConfig((p) => { const n = { ...p }; delete n[name]; return n; });
    } catch (err) {
      toast({ title: err.message || 'Failed to save', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSavingSet((p) => { const n = new Set(p); n.delete(name); return n; });
    }
  };

  const handleDeleteConversion = async (name) => {
    const saved = conversionMap[name];
    if (!saved) {
      setLocalConfig((p) => { const n = { ...p }; delete n[name]; return n; });
      return;
    }
    setSavingSet((p) => new Set([...p, name]));
    try {
      const d = await axios.delete(`${API_URL}/event-conversations/${saved._id}`, { headers: getApiHeaders() }).then(r => r.data);
      if (!d.status) throw new Error(d.message);
      setConversionMap((p) => { const n = { ...p }; delete n[name]; return n; });
      setLocalConfig((p) => { const n = { ...p }; delete n[name]; return n; });
      toast({ title: 'Conversion removed', status: 'info', duration: 2000 });
    } catch (err) {
      toast({ title: err.message || 'Failed to remove', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSavingSet((p) => { const n = new Set(p); n.delete(name); return n; });
    }
  };

  // ── Group assignment ──────────────────────────────────────────────────────
  const handleGroupAssign = (name, groupId) => {
    if (groupId === '__create__') { setPendingAssignEventName(name); quickCreateModal.onOpen(); return; }
    updateConfig(name, { conversion_group: groupId === 'none' ? null : groupId });
  };

  // ── Groups CRUD ───────────────────────────────────────────────────────────
  const handleCreateGroup = async (suggested) => {
    const name = suggested ? suggested.name : newGroupName;
    const color = suggested ? suggested.color : newGroupColor;
    if (!name) return;
    setSavingGroup(true);
    try {
      const d = await axios.post(`${API_URL}/conversation-groups`, { name, color, status: 'active' }, { headers: getApiHeaders() }).then(r => r.data);
      if (!d.status) throw new Error(d.message);
      const newGroup = d.data.conversation_group;
      setGroups((p) => [...p, newGroup]);
      if (pendingAssignEventName) { updateConfig(pendingAssignEventName, { conversion_group: newGroup._id }); setPendingAssignEventName(null); quickCreateModal.onClose(); }
      resetGroupForm();
      toast({ title: 'Group created', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: err.message || 'Failed to create group', status: 'error', duration: 3000, isClosable: true });
    } finally { setSavingGroup(false); }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !newGroupName) return;
    setSavingGroup(true);
    try {
      const d = await axios.put(`${API_URL}/conversation-groups/${editingGroup._id}`, { name: newGroupName, color: newGroupColor }, { headers: getApiHeaders() }).then(r => r.data);
      if (!d.status) throw new Error(d.message);
      setGroups((p) => p.map((g) => (g._id === editingGroup._id ? d.data.conversation_group : g)));
      resetGroupForm();
      toast({ title: 'Group updated', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: err.message || 'Failed to update group', status: 'error', duration: 3000, isClosable: true });
    } finally { setSavingGroup(false); }
  };

  const handleDeleteGroup = async () => {
    setDeletingGroup(true);
    try {
      const d = await axios.delete(`${API_URL}/conversation-groups/${deleteGroupId}`, { headers: getApiHeaders() }).then(r => r.data);
      if (!d.status) throw new Error(d.message);
      setGroups((p) => p.filter((g) => g._id !== deleteGroupId));
      setDeleteGroupId(null);
      deleteGroupModal.onClose();
      toast({ title: 'Group deleted', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: err.message || 'Failed to delete group', status: 'error', duration: 3000, isClosable: true });
    } finally { setDeletingGroup(false); }
  };

  const handleEditGroup = (group) => { setEditingGroup(group); setNewGroupName(group.name); setNewGroupColor(group.color || '#3B82F6'); };
  const resetGroupForm = () => { setNewGroupName(''); setNewGroupColor('#3B82F6'); setEditingGroup(null); };

  // ── Derived values ────────────────────────────────────────────────────────
  const conversionCount = events.filter((ev) => isConversionEvent(ev.name)).length;
  const hasMultiplePrimary = events.filter((ev) => getEffectiveConfig(ev.name)?.conversion_type === 'primary').length > 1;

  const filteredEvents = events.filter((ev) => {
    const matchSearch = ev.display_name.toLowerCase().includes(searchQuery.toLowerCase()) || ev.name.toLowerCase().includes(searchQuery.toLowerCase());
    const isConv = isConversionEvent(ev.name);
    const cfg = getEffectiveConfig(ev.name);
    const matchStatus = filterStatus === 'all' ? true : filterStatus === 'conversion' ? isConv : !isConv;
    const matchGroup = filterGroup === 'all-groups' ? true : filterGroup === 'ungrouped' ? (isConv && !cfg?.conversion_group) : cfg?.conversion_group === filterGroup;
    return matchSearch && matchStatus && matchGroup;
  });

  // ── Render ────────────────────────────────────────────────────────────────
  const inputStyle = {
    borderRadius: 'lg', borderColor: 'gray.200', bg: 'gray.50', fontSize: 'sm',
    _focus: { bg: 'white', borderColor: 'blue.400', boxShadow: '0 0 0 1px #93C5FD' },
    _hover: { borderColor: 'gray.300' },
  };

  return (
    <>
      <Header />
      <BoxWrapper>

        {/* Page Header */}
        <Flex justify="space-between" align="center" mb={6}>
          <HStack spacing={3}>
            <Box w={10} h={10} borderRadius="xl" bg="blue.50" border="1px solid" borderColor="blue.100"
              display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <Target size={20} color="#3B82F6" />
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="gray.800" lineHeight="1.2">Conversion Events</Text>
              <Text fontSize="sm" color="gray.500" mt={0.5}>Define which tracked events represent business success</Text>
            </Box>
          </HStack>
          <HStack spacing={2}>
            <Button leftIcon={<RefreshCw size={13} />} size="sm" variant="ghost" color="gray.500"
              onClick={loadAll} isLoading={loading} borderRadius="lg">
              Refresh
            </Button>
            <Button leftIcon={<Folder size={13} />} size="sm" variant="outline" borderRadius="lg"
              borderColor="gray.200" _hover={{ bg: 'gray.50' }} onClick={manageGroupsModal.onOpen}>
              Manage Groups
            </Button>
          </HStack>
        </Flex>

        {/* Stats chips */}
        <HStack spacing={2} mb={5}>
          <HStack px={3} py={1.5} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.100" spacing={1.5}>
            <Zap size={12} color="#3B82F6" />
            <Text fontSize="xs" fontWeight="600" color="blue.700">{events.length} Events</Text>
          </HStack>
          <HStack px={3} py={1.5} bg="green.50" borderRadius="lg" border="1px solid" borderColor="green.100" spacing={1.5}>
            <Target size={12} color="#10B981" />
            <Text fontSize="xs" fontWeight="600" color="green.700">{conversionCount} Conversions</Text>
          </HStack>
          {groups.length > 0 && (
            <HStack px={3} py={1.5} bg="purple.50" borderRadius="lg" border="1px solid" borderColor="purple.100" spacing={1.5}>
              <Folder size={12} color="#8B5CF6" />
              <Text fontSize="xs" fontWeight="600" color="purple.700">{groups.length} Groups</Text>
            </HStack>
          )}
        </HStack>

        {/* Multiple primary warning */}
        {hasMultiplePrimary && (
          <Alert status="warning" borderRadius="xl" mb={4} fontSize="sm">
            <AlertIcon />
            <AlertDescription>
              <strong>Multiple primary conversions detected.</strong> We recommend one primary conversion per campaign.
            </AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Box p={4} borderRadius="xl" bg="white" border="1px solid" borderColor="gray.200"
          boxShadow="0 1px 4px rgba(0,0,0,0.04)" mb={5}>
          <Flex gap={3} align="center" flexWrap="wrap">
            <InputGroup flex={1} minW="200px" size="sm">
              <InputLeftElement pointerEvents="none"><Search size={13} color="#94A3B8" /></InputLeftElement>
              <Input placeholder="Search events…" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} {...inputStyle} />
            </InputGroup>
            <Select size="sm" w="190px" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} {...inputStyle}>
              <option value="all">All Events</option>
              <option value="conversion">Marked as Conversion</option>
              <option value="not_marked">Not Marked</option>
            </Select>
            <Select size="sm" w="190px" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)} {...inputStyle}>
              <option value="all-groups">All Groups</option>
              <option value="ungrouped">Ungrouped Conversions</option>
              {groups.map((g) => <option key={g._id} value={g._id}>{g.name}</option>)}
            </Select>
          </Flex>
        </Box>

        {/* Event list */}
        {loading ? (
          <VStack spacing={3} align="stretch">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height="72px" borderRadius="xl" startColor="gray.100" endColor="gray.50" />
            ))}
          </VStack>
        ) : filteredEvents.length === 0 ? (
          <Flex direction="column" align="center" justify="center" py={20} gap={3}>
            <Box w={16} h={16} borderRadius="2xl" bg="gray.50" border="1px solid" borderColor="gray.200"
              display="flex" alignItems="center" justifyContent="center">
              <Target size={28} color="#CBD5E1" />
            </Box>
            <Box textAlign="center">
              <Text fontWeight="semibold" color="gray.700" mb={1}>No events found</Text>
              <Text fontSize="sm" color="gray.400">Try adjusting your search or filters</Text>
            </Box>
            {(searchQuery || filterStatus !== 'all' || filterGroup !== 'all-groups') && (
              <Button size="sm" variant="outline" borderRadius="lg"
                onClick={() => { setFilterStatus('all'); setFilterGroup('all-groups'); setSearchQuery(''); }}>
                Clear filters
              </Button>
            )}
          </Flex>
        ) : (
          <VStack spacing={2} align="stretch">
            {filteredEvents.map((event) => {
              const cfg = getEffectiveConfig(event.name);
              const isConv = isConversionEvent(event.name);
              const dirty = isDirty(event.name);
              const saving = savingSet.has(event.name);
              const pendingRemoval = localConfig[event.name] === null;
              const group = cfg?.conversion_group ? getGroupById(cfg.conversion_group) : null;
              const attrs = eventAttributes[event.name] || [];
              const attrsIsLoading = attributesLoading.has(event.name);

              return (
                <Box
                  key={event.name}
                  borderRadius="xl" bg="white" overflow="hidden"
                  border="1px solid"
                  borderColor={dirty ? 'blue.200' : isConv ? 'green.200' : 'gray.200'}
                  borderLeft="3px solid"
                  borderLeftColor={dirty ? 'blue.400' : isConv && !pendingRemoval ? 'green.400' : 'transparent'}
                  boxShadow={isConv ? '0 2px 8px rgba(16,185,129,0.08)' : '0 1px 4px rgba(0,0,0,0.04)'}
                  transition="all 0.15s"
                  _hover={{ boxShadow: isConv ? '0 4px 16px rgba(16,185,129,0.12)' : '0 4px 12px rgba(0,0,0,0.08)' }}
                >
                  <Box p={4}>
                    {/* Card header */}
                    <Flex align="center" justify="space-between" gap={3}>
                      <HStack spacing={3} flex={1} minW={0}>
                        <Box w={9} h={9} borderRadius="lg" flexShrink={0}
                          bg={isConv ? 'green.50' : 'gray.50'}
                          border="1px solid" borderColor={isConv ? 'green.100' : 'gray.200'}
                          display="flex" alignItems="center" justifyContent="center">
                          <Zap size={15} color={isConv ? '#10B981' : '#94A3B8'} />
                        </Box>
                        <Box minW={0}>
                          <Text fontWeight="600" fontSize="sm" color="gray.800" noOfLines={1}>{event.display_name}</Text>
                          <Text fontSize="11px" fontFamily="mono" color="gray.400" noOfLines={1}>{event.name}</Text>
                        </Box>
                      </HStack>

                      <HStack spacing={3} flexShrink={0}>
                        {isConv && !pendingRemoval && (
                          <HStack spacing={1.5}>
                            {group && (
                              <Tag size="sm" borderRadius="full"
                                style={{ border: `1px solid ${group.color}50`, color: group.color, backgroundColor: `${group.color}15` }}>
                                <TagLabel fontSize="10px" fontWeight="600">{group.name}</TagLabel>
                              </Tag>
                            )}
                            <Tag size="sm" borderRadius="full"
                              colorScheme={cfg?.conversion_type === 'primary' ? 'purple' : 'blue'} variant="subtle">
                              <TagLabel fontSize="10px" fontWeight="600">
                                {cfg?.conversion_type === 'primary' ? '★ Primary' : 'Secondary'}
                              </TagLabel>
                            </Tag>
                          </HStack>
                        )}

                        <HStack spacing={1.5}>
                          <Text fontSize="xs" color="gray.400" fontWeight="500" userSelect="none">Conversion</Text>
                          <Switch isChecked={isConv} onChange={() => handleToggle(event.name)} colorScheme="green" size="sm" />
                        </HStack>

                        {(isConv || !!conversionMap[event.name]) && (
                          <Menu placement="bottom-end">
                            <MenuButton
                              as={IconButton}
                              icon={<MoreVertical size={14} />}
                              variant="ghost" size="xs" borderRadius="md"
                              color="gray.400" _hover={{ color: 'gray.700', bg: 'gray.100' }}
                              aria-label="options" isDisabled={saving}
                            />
                            <MenuList minW="160px" shadow="lg" borderRadius="xl"
                              border="1px solid" borderColor="gray.200" py={1}>
                              <MenuItem
                                icon={<Trash2 size={13} />}
                                color="red.500" fontSize="sm"
                                borderRadius="lg" mx={1} _hover={{ bg: 'red.50' }}
                                onClick={() => handleDeleteConversion(event.name)}
                              >
                                Remove Conversion
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        )}
                      </HStack>
                    </Flex>

                    {/* Expanded config */}
                    {(isConv || pendingRemoval) && (
                      <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.100">
                        {pendingRemoval ? (
                          <Flex align="center" justify="space-between" bg="red.50" px={4} py={3} borderRadius="xl"
                            border="1px solid" borderColor="red.100">
                            <HStack spacing={2}>
                              <Trash2 size={13} color="#EF4444" />
                              <Text fontSize="sm" color="red.600" fontWeight="500">Conversion mapping will be removed</Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Button size="xs" variant="ghost" borderRadius="lg" onClick={() => handleCancel(event.name)} isDisabled={saving}>Cancel</Button>
                              <Button size="xs" colorScheme="red" borderRadius="lg" isLoading={saving} onClick={() => handleSave(event.name)}>Confirm Remove</Button>
                            </HStack>
                          </Flex>
                        ) : (
                          <>
                            <HStack spacing={4} align="flex-start" wrap="wrap">
                              {/* Conversion Type */}
                              <Box flexShrink={0}>
                                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2.5}>
                                  Conversion Type
                                </Text>
                                <RadioGroup value={cfg?.conversion_type || 'secondary'}
                                  onChange={(val) => updateConfig(event.name, { conversion_type: val })}>
                                  <HStack spacing={3}>
                                    <Radio value="primary" size="sm" colorScheme="purple">
                                      <Text fontSize="xs" fontWeight="500" color="gray.700">Primary</Text>
                                    </Radio>
                                    <Radio value="secondary" size="sm" colorScheme="blue">
                                      <Text fontSize="xs" fontWeight="500" color="gray.700">Secondary</Text>
                                    </Radio>
                                  </HStack>
                                </RadioGroup>
                              </Box>

                              <Box w="1px" alignSelf="stretch" bg="gray.200" flexShrink={0} />

                              {/* Group */}
                              <Box flexShrink={0}>
                                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2.5}>
                                  Group <Text as="span" fontWeight="400" color="gray.300" textTransform="none">optional</Text>
                                </Text>
                                <Select size="sm" w="250px" value={cfg?.conversion_group || 'none'}
                                  onChange={(e) => handleGroupAssign(event.name, e.target.value)} {...inputStyle}>
                                  <option value="none">No Group</option>
                                  {groups.map((g) => <option key={g._id} value={g._id}>{g.name}</option>)}
                                  <option value="__create__">+ Create new group…</option>
                                </Select>
                              </Box>

                              <Box w="1px" alignSelf="stretch" bg="gray.200" flexShrink={0} />

                              {/* Value Source */}
                              <Box flex={1}>
                                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2.5}>
                                  Value Source
                                </Text>
                              <HStack spacing={2} align="center">
                                <Select size="sm" w="250px" flexShrink={0} value={cfg?.value_config?.source_type || 'no_value'}
                                  onChange={(e) => {
                                    const t = e.target.value;
                                    const method = t === 'no_value' ? 'count' : 'sum';
                                    updateConfig(event.name, { value_config: { source_type: t, method } });
                                    if (t === 'from_property') fetchAttributes(event.name);
                                  }} {...inputStyle}>
                                  <option value="no_value">No Value</option>
                                  <option value="fixed_value">Fixed Value</option>
                                  <option value="from_property">Event Property</option>
                                </Select>

                                {/* Method: Count / Sum */}
                                {(() => {
                                  const src = cfg?.value_config?.source_type || 'no_value';
                                  const isEditable = src === 'from_property';
                                  const methodVal = src === 'no_value' ? 'count' : (cfg?.value_config?.method || 'sum');
                                  return (
                                    <Select size="sm" w="90px" flexShrink={0}
                                      value={methodVal}
                                      isDisabled={!isEditable}
                                      onChange={(e) => updateConfig(event.name, {
                                        value_config: { ...cfg?.value_config, method: e.target.value },
                                      })}
                                      {...inputStyle}
                                      opacity={isEditable ? 1 : 0.55}
                                      cursor={isEditable ? 'auto' : 'not-allowed'}>
                                      {src === 'no_value' ? (
                                        <option value="count">Count</option>
                                      ) : src === 'fixed_value' ? (
                                        <option value="sum">Sum</option>
                                      ) : (
                                        <>
                                          <option value="count">Count</option>
                                          <option value="sum">Sum</option>
                                        </>
                                      )}
                                    </Select>
                                  );
                                })()}

                                {cfg?.value_config?.source_type === 'fixed_value' && (
                                  <Input size="sm" type="number" step="0.01" placeholder="e.g. 10.00"
                                    w="110px" flexShrink={0}
                                    value={cfg.value_config.value || ''}
                                    onChange={(e) => updateConfig(event.name, { value_config: { ...cfg.value_config, value: e.target.value } })}
                                    {...inputStyle} />
                                )}

                                {cfg?.value_config?.source_type === 'from_property' && (
                                  attrsIsLoading ? (
                                    <HStack spacing={2} px={3} py={1.5} bg="gray.50" borderRadius="lg"
                                      border="1px solid" borderColor="gray.200" flexShrink={0}>
                                      <Spinner size="xs" color="blue.400" />
                                      <Text fontSize="xs" color="gray.400">Loading…</Text>
                                    </HStack>
                                  ) : (
                                    <Select size="sm" placeholder="Select property"
                                      w="160px" flexShrink={0}
                                      value={cfg.value_config.value || ''}
                                      onFocus={() => fetchAttributes(event.name)}
                                      onChange={(e) => updateConfig(event.name, { value_config: { ...cfg.value_config, value: e.target.value } })}
                                      {...inputStyle}>
                                      {cfg.value_config.value && !attrs.find((a) => a.name === cfg.value_config.value) && (
                                        <option value={cfg.value_config.value}>{cfg.value_config.value}</option>
                                      )}
                                      {attrs.map((attr) => (
                                        <option key={attr.name} value={attr.name}>
                                          {attr.name}{attr.type !== 'string' ? ` (${attr.type})` : ''}
                                        </option>
                                      ))}
                                    </Select>
                                  )
                                )}
                              </HStack>
                              </Box>
                            </HStack>

                            {dirty && (
                              <Flex justify="flex-end" mt={4} gap={2}>
                                <Button size="sm" variant="ghost" borderRadius="lg" color="gray.500"
                                  onClick={() => handleCancel(event.name)} isDisabled={saving}>
                                  Cancel
                                </Button>
                                <Button size="sm" colorScheme="blue" borderRadius="lg"
                                  isLoading={saving} onClick={() => handleSave(event.name)}>
                                  Save Changes
                                </Button>
                              </Flex>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </VStack>
        )}

        <Text fontSize="xs" color="gray.400" textAlign="center" mt={6} pt={4} borderTop="1px solid" borderColor="gray.100">
          Events are never modified. Conversion settings can be changed anytime without data loss.
        </Text>
      </BoxWrapper>

      {/* ── Manage Groups Modal ──────────────────────────────────────────────── */}
      <Modal isOpen={manageGroupsModal.isOpen} onClose={manageGroupsModal.onClose} size="md">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" overflow="hidden">
          <ModalHeader pb={3} borderBottom="1px solid" borderColor="gray.100" fontSize="md">
            <HStack spacing={2}><Folder size={15} color="#3B82F6" /><Text>Manage Conversion Groups</Text></HStack>
          </ModalHeader>
          <ModalCloseButton borderRadius="lg" />
          <ModalBody pb={6} pt={4}>
            <Alert status="info" borderRadius="xl" mb={5} fontSize="xs">
              <AlertIcon boxSize={4} />
              Groups are optional — used for organization and reporting only.
            </Alert>

            <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4} bg="gray.50" mb={4}>
              <Text fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
                {editingGroup ? 'Edit Group' : 'New Group'}
              </Text>
              <VStack spacing={3} align="stretch">
                <Input size="sm" placeholder="Group name (e.g. Revenue, Marketing)"
                  value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                  borderRadius="lg" borderColor="gray.200" bg="white" _focus={{ borderColor: 'var(--brand-primary)' }} />
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={2}>Color</Text>
                  <HStack spacing={2}>
                    {colorPresets.map((preset) => (
                      <Box key={preset.value} w={7} h={7} borderRadius="full" bg={preset.value} cursor="pointer"
                        onClick={() => setNewGroupColor(preset.value)}
                        outline={newGroupColor === preset.value ? '3px solid' : 'none'}
                        outlineColor="blue.400" outlineOffset="2px" title={preset.name}
                        transition="transform 0.1s" _hover={{ transform: 'scale(1.15)' }} />
                    ))}
                  </HStack>
                </Box>
                <HStack>
                  {editingGroup ? (
                    <>
                      <Button size="sm" flex={1} colorScheme="blue" borderRadius="lg"
                        isDisabled={!newGroupName} isLoading={savingGroup} onClick={handleUpdateGroup}>
                        Update Group
                      </Button>
                      <Button size="sm" variant="outline" borderRadius="lg" onClick={resetGroupForm} isDisabled={savingGroup}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" w="full" colorScheme="blue" borderRadius="lg"
                      isDisabled={!newGroupName} isLoading={savingGroup}
                      leftIcon={<Plus size={13} />} onClick={() => handleCreateGroup()}>
                      Add Group
                    </Button>
                  )}
                </HStack>
              </VStack>
            </Box>

            {groups.length > 0 ? (
              <Box>
                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={3}>
                  Your Groups ({groups.length})
                </Text>
                <VStack spacing={2} align="stretch">
                  {groups.map((group) => (
                    <Box key={group._id} borderRadius="xl" bg="white"
                      border="1px solid" borderColor="gray.200"
                      borderLeft="3px solid" borderLeftColor={group.color || '#3B82F6'} p={3}>
                      <Flex align="center" justify="space-between">
                        <HStack spacing={2}>
                          <Box w={2.5} h={2.5} borderRadius="full" bg={group.color || '#3B82F6'} flexShrink={0} />
                          <Text fontSize="sm" fontWeight="500" color="gray.700">{group.name}</Text>
                        </HStack>
                        <Menu>
                          <MenuButton as={IconButton} icon={<MoreVertical size={13} />}
                            variant="ghost" size="xs" borderRadius="md"
                            color="gray.400" _hover={{ color: 'gray.700', bg: 'gray.100' }} aria-label="group options" />
                          <MenuList minW="130px" shadow="lg" borderRadius="xl"
                            border="1px solid" borderColor="gray.200" py={1}>
                            <MenuItem icon={<Edit2 size={12} />} fontSize="xs" borderRadius="lg" mx={1}
                              onClick={() => handleEditGroup(group)}>Edit</MenuItem>
                            <MenuItem icon={<Trash2 size={12} />} fontSize="xs" color="red.500"
                              borderRadius="lg" mx={1} _hover={{ bg: 'red.50' }}
                              onClick={() => { setDeleteGroupId(group._id); deleteGroupModal.onOpen(); }}>Delete</MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              </Box>
            ) : (
              <Flex direction="column" align="center" py={8} gap={2}>
                <Folder size={26} color="#CBD5E1" />
                <Text fontSize="sm" color="gray.400">No groups yet. Create your first group above.</Text>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* ── Quick Create Group Modal ─────────────────────────────────────────── */}
      <Modal isOpen={quickCreateModal.isOpen}
        onClose={() => { quickCreateModal.onClose(); setPendingAssignEventName(null); resetGroupForm(); }} size="sm">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader pb={3} borderBottom="1px solid" borderColor="gray.100" fontSize="md">Create New Group</ModalHeader>
          <ModalCloseButton borderRadius="lg" />
          <ModalBody pt={4}>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>Group Name</Text>
                <Input size="sm" placeholder="e.g. Marketing, Revenue" value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  borderRadius="lg" borderColor="gray.200" _focus={{ borderColor: 'var(--brand-primary)' }} />
              </Box>
              <Box>
                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>Color</Text>
                <HStack spacing={2}>
                  {colorPresets.map((preset) => (
                    <Box key={preset.value} w={7} h={7} borderRadius="full" bg={preset.value} cursor="pointer"
                      onClick={() => setNewGroupColor(preset.value)}
                      outline={newGroupColor === preset.value ? '3px solid' : 'none'}
                      outlineColor="blue.400" outlineOffset="2px" title={preset.name}
                      _hover={{ transform: 'scale(1.15)' }} transition="transform 0.1s" />
                  ))}
                </HStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2} borderTop="1px solid" borderColor="gray.100" pt={3}>
            <Button size="sm" variant="ghost" borderRadius="lg"
              onClick={() => { quickCreateModal.onClose(); setPendingAssignEventName(null); resetGroupForm(); }}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="blue" borderRadius="lg" leftIcon={<Plus size={13} />}
              isDisabled={!newGroupName} isLoading={savingGroup} onClick={() => handleCreateGroup()}>
              Create Group
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Delete Group Confirm Modal ───────────────────────────────────────── */}
      <Modal isOpen={deleteGroupModal.isOpen}
        onClose={() => { deleteGroupModal.onClose(); setDeleteGroupId(null); }} size="sm">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader pb={3} borderBottom="1px solid" borderColor="gray.100" fontSize="md">
            <HStack spacing={2}><Trash2 size={15} color="#EF4444" /><Text>Delete Group?</Text></HStack>
          </ModalHeader>
          <ModalCloseButton borderRadius="lg" />
          <ModalBody pt={4}>
            <Text fontSize="sm" color="gray.600" lineHeight="1.7">
              This will permanently delete the group. Events linked to it will remain active but lose their group assignment.
            </Text>
          </ModalBody>
          <ModalFooter gap={2} borderTop="1px solid" borderColor="gray.100" pt={3}>
            <Button size="sm" variant="ghost" borderRadius="lg" isDisabled={deletingGroup}
              onClick={() => { deleteGroupModal.onClose(); setDeleteGroupId(null); }}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="red" borderRadius="lg" isLoading={deletingGroup} onClick={handleDeleteGroup}>
              Delete Group
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
